import { useState, useEffect, useCallback } from 'react';
import { getApiUrl } from '../config/api';

const useQuotations = (token) => {
  const [quotations, setQuotations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('timeCreated');
  const [sortDirection, setSortDirection] = useState('desc'); // Most recent first
  const [filters, setFilters] = useState({
    clientId: '',
    quoteStatus: ''
  });
  const [lastFetchTime, setLastFetchTime] = useState(null);

  // localStorage key for caching quotations
  const CACHE_KEY = 'aerotubos_quotations_cache';
  const CACHE_TIMESTAMP_KEY = 'aerotubos_quotations_cache_timestamp';

  // Load quotations from localStorage
  const loadFromCache = useCallback(() => {
    try {
      const cachedData = localStorage.getItem(CACHE_KEY);
      const cachedTimestamp = localStorage.getItem(CACHE_TIMESTAMP_KEY);
      
      if (cachedData && cachedTimestamp) {
        const quotations = JSON.parse(cachedData);
        const timestamp = parseInt(cachedTimestamp);
        setQuotations(quotations);
        setLastFetchTime(timestamp);
        console.log('📦 Loaded quotations from cache:', quotations.length, 'items');
        return quotations;
      }
    } catch (error) {
      console.error('Error loading quotations from cache:', error);
    }
    console.log('📦 No cached quotations found');
    return [];
  }, []);

  // Save quotations to localStorage
  const saveToCache = useCallback((quotations) => {
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify(quotations));
      localStorage.setItem(CACHE_TIMESTAMP_KEY, Date.now().toString());
      setLastFetchTime(Date.now());
    } catch (error) {
      console.error('Error saving quotations to cache:', error);
    }
  }, []);

  // Fetch quotations from API
  const fetchQuotations = useCallback(async (customFilters = {}) => {
    if (!token) return;

    setLoading(true);
    setError(null);

    try {
      // Build query parameters
      const queryParams = new URLSearchParams();
      
      if (customFilters.clientId || filters.clientId) {
        queryParams.append('clientId', customFilters.clientId || filters.clientId);
      }
      if (customFilters.quoteStatus || filters.quoteStatus) {
        queryParams.append('quoteStatus', customFilters.quoteStatus || filters.quoteStatus);
      }
      
      // Add limit to prevent large responses
      queryParams.append('limit', '1000');
      
      const queryString = queryParams.toString();
      const url = queryString ? `${getApiUrl('/api/quotations')}?${queryString}` : getApiUrl('/api/quotations');
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      const quotationsData = data.quotations || [];
      setQuotations(quotationsData);
      saveToCache(quotationsData);
    } catch (err) {
      console.error('Error fetching quotations:', err);
      setError('Error fetching quotations: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [token, filters, saveToCache]);

  // Fetch a single quotation by ID
  const fetchQuotationById = useCallback(async (quotationId) => {
    if (!token || !quotationId) return null;

    try {
      const response = await fetch(getApiUrl(`/api/quotations/${quotationId}`), {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      const quotation = await response.json();
      return quotation;
    } catch (err) {
      console.error('Error fetching quotation:', err);
      setError('Error fetching quotation: ' + err.message);
      return null;
    }
  }, [token]);

  // Update filters
  const updateFilters = useCallback((newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  }, []);

  // Clear filters
  const clearFilters = useCallback(() => {
    setFilters({ clientId: '', quoteStatus: '' });
  }, []);

  // Handle sorting
  const handleSort = useCallback((field) => {
    const direction = (sortField === field && sortDirection === 'asc') ? 'desc' : 'asc';
    setSortField(field);
    setSortDirection(direction);
  }, [sortField, sortDirection]);

  const getSortIndicator = useCallback((field) => {
    if (sortField === field) {
      return sortDirection === 'asc' ? ' ⬆' : ' ⬇';
    }
    return '';
  }, [sortField, sortDirection]);

  // Filtering logic
  const filteredQuotations = quotations.filter(quotation => {
    // Search term filtering
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch = Object.values(quotation).some(value => {
        if (typeof value === 'object' && value !== null) {
          return Object.values(value).some(subValue => 
            String(subValue).toLowerCase().includes(searchLower)
          );
        }
        return String(value).toLowerCase().includes(searchLower);
      });
      if (!matchesSearch) return false;
    }

    // Client ID filtering
    if (filters.clientId) {
      if (quotation.clientId !== filters.clientId) {
        return false;
      }
    }

    // Status filtering
    if (filters.quoteStatus) {
      if (quotation.quoteStatus !== filters.quoteStatus) {
        return false;
      }
    }

    return true;
  });

  // Sorting logic
  const sortedQuotations = [...filteredQuotations].sort((a, b) => {
    if (!sortField) return 0;

    let aValue, bValue;

    // Handle nested fields
    if (sortField.includes('.')) {
      const [parent, child] = sortField.split('.');
      aValue = a[parent]?.[child];
      bValue = b[parent]?.[child];
    } else {
      aValue = a[sortField];
      bValue = b[sortField];
    }

    // Handle different data types
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return sortDirection === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
    }
    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
    }
    
    // Handle date-like strings (timeCreated)
    if (sortField === 'timeCreated') {
      const aTime = parseFloat(aValue) || 0;
      const bTime = parseFloat(bValue) || 0;
      return sortDirection === 'asc' ? aTime - bTime : bTime - aTime;
    }
    
    return 0;
  });

  // Utility functions
  const formatDate = useCallback((timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(parseFloat(timestamp) * 1000);
    return date.toLocaleDateString('pt-BR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  }, []);

  const formatCurrency = useCallback((value, currency = 'BRL') => {
    if (!value) return 'R$ 0,00';
    const numValue = parseFloat(value);
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: currency === 'AUD$' ? 'AUD' : 'BRL'
    }).format(numValue);
  }, []);

  const getQuotationStatusColor = useCallback((status) => {
    switch (status?.toLowerCase()) {
      case 'open': return '#28a745'; // Green
      case 'expired': return '#dc3545'; // Red
      case 'accepted': return '#007bff'; // Blue
      case 'rejected': return '#6c757d'; // Gray
      default: return '#6c757d';
    }
  }, []);

  const calculateQuotationTotal = useCallback((quotation) => {
    if (!quotation.itens || !Array.isArray(quotation.itens)) return 0;
    
    const subtotal = quotation.itens.reduce((total, item) => {
      const price = parseFloat(item.produto?.price || 0);
      const quantity = parseFloat(item.quantity || 0);
      return total + (price * quantity);
    }, 0);
    
    const discount = parseFloat(quotation.desconto || 0);
    const freight = parseFloat(quotation.valorTransporte || 0);
    
    return subtotal - discount + freight;
  }, []);

  // Get cache information
  const getCacheInfo = useCallback(() => {
    if (!lastFetchTime) return null;
    
    const now = Date.now();
    const timeDiff = now - lastFetchTime;
    const minutesAgo = Math.floor(timeDiff / (1000 * 60));
    
    return {
      lastFetch: lastFetchTime,
      minutesAgo,
      isStale: minutesAgo > 30 // Consider stale after 30 minutes
    };
  }, [lastFetchTime]);

  // Clear cache
  const clearCache = useCallback(() => {
    try {
      localStorage.removeItem(CACHE_KEY);
      localStorage.removeItem(CACHE_TIMESTAMP_KEY);
      setQuotations([]);
      setLastFetchTime(null);
      console.log('🗑️ Quotations cache cleared');
    } catch (error) {
      console.error('Error clearing cache:', error);
    }
  }, []);

  // Load quotations from cache on mount, only fetch if no cache
  useEffect(() => {
    if (token) {
      const cachedQuotations = loadFromCache();
      // Only fetch from API if there are no cached quotations
      if (cachedQuotations.length === 0) {
        console.log('🔄 No cached quotations found, fetching from API...');
        fetchQuotations();
      } else {
        console.log('✅ Using cached quotations, no API fetch needed');
      }
    }
  }, [token, loadFromCache, fetchQuotations]);

  return {
    quotations: sortedQuotations,
    loading,
    error,
    searchTerm,
    setSearchTerm,
    filters,
    updateFilters,
    clearFilters,
    handleSort,
    getSortIndicator,
    fetchQuotations,
    fetchQuotationById,
    formatDate,
    formatCurrency,
    getQuotationStatusColor,
    calculateQuotationTotal,
    getCacheInfo,
    clearCache,
    lastFetchTime
  };
};

export default useQuotations;
